package presentation.viewmodel

import domain.model.ActionCard
import domain.model.PRViewMode
import domain.usecase.ManageActionsUseCase
import domain.usecase.LoginUseCase
import domain.usecase.LogoutUseCase
import domain.usecase.ObserveAuthenticationStateUseCase
import domain.usecase.GetPullRequestsUseCase
import domain.usecase.ObserveThemeUseCase
import domain.usecase.ChangeThemeUseCase
import domain.usecase.DiagnoseOrganizationAccessUseCase
import domain.usecase.GetPRInsightsUseCase
import presentation.state.AppState
import ui.theme.ThemeMode
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class AppViewModel(
    private val manageActionsUseCase: ManageActionsUseCase,
    private val loginUseCase: LoginUseCase,
    private val logoutUseCase: LogoutUseCase,
    private val observeAuthenticationStateUseCase: ObserveAuthenticationStateUseCase,
    private val getPullRequestsUseCase: GetPullRequestsUseCase,
    private val observeThemeUseCase: ObserveThemeUseCase,
    private val changeThemeUseCase: ChangeThemeUseCase,
    private val diagnoseOrganizationAccessUseCase: DiagnoseOrganizationAccessUseCase,
    private val getPRInsightsUseCase: GetPRInsightsUseCase
) {
    
    // Create a coroutine scope for the ViewModel
    private val viewModelJob = Job()
    private val viewModelScope = CoroutineScope(viewModelJob)
    
    // StateFlow for reactive state management
    private val _state = MutableStateFlow(AppState())
    val state: StateFlow<AppState> = _state.asStateFlow()
    
    private fun updateState(newState: AppState) {
        _state.tryEmit(newState)
    }
    
    init {
        loadInitialData()
    }
    
    private fun loadInitialData() {
        viewModelScope.launch {
            // Load actions
            val actions = manageActionsUseCase.getActions()
            updateState(_state.value.copy(actions = actions))
            
            // Observe reactive data flows
            observeAuthenticationState()
            observeTheme()
            
            // Process OAuth callback (in case user just came back from GitHub login)
            processOAuthCallback()
        }
    }
    
    /**
     * Observe theme changes reactively (data down)
     */
    private fun observeTheme() {
        viewModelScope.launch {
            observeThemeUseCase.execute().collect { theme ->
                println("AppViewModel: Theme changed: $theme")
                updateState(_state.value.copy(currentTheme = theme))
            }
        }
    }
    
    /**
     * Observe authentication state reactively (data down)
     * No polling needed - all authentication changes flow reactively through the repository
     */
    private fun observeAuthenticationState() {
        viewModelScope.launch {
            observeAuthenticationStateUseCase.execute().collect { isAuthenticated ->
                println("AppViewModel: Authentication state changed: $isAuthenticated")
                updateState(_state.value.copy(isAuthenticated = isAuthenticated))
                
                if (isAuthenticated && _state.value.pullRequests.isEmpty()) {
                    // Automatically fetch PRs when authenticated
                    fetchPullRequests()
                    // Load enhanced features following CLEAN architecture
                    loadEnhancedFeatures()
                } else if (!isAuthenticated) {
                    // Clear all data when not authenticated
                    updateState(_state.value.copy(
                        pullRequests = emptyList(),
                        enhancedPRs = emptyList(),
                        organizations = emptyList(),
                        pendingInvitations = emptyList(),
                        isEnhancedLoading = false
                    ))
                }
            }
        }
    }
    
    private fun fetchPullRequests() {
        viewModelScope.launch {
            try {
                println("AppViewModel: Fetching pull requests...")
                updateState(_state.value.copy(isLoading = true))
                val prs = getPullRequestsUseCase()
                println("AppViewModel: Fetched ${prs.size} PRs")
                updateState(_state.value.copy(
                    pullRequests = prs,
                    isLoading = false
                ))
            } catch (e: Exception) {
                println("AppViewModel: Error fetching PRs: ${e.message}")
                updateState(_state.value.copy(
                    isLoading = false,
                    errorMessage = "Failed to fetch pull requests: ${e.message}"
                ))
            }
        }
    }
    
    fun refreshPullRequests() {
        if (_state.value.isAuthenticated) {
            fetchPullRequests()
        }
    }
    
    fun addAction(title: String, description: String, url: String, icon: String?) {
        val newAction = ActionCard(
            id = generateId(),
            title = title,
            description = description,
            url = url,
            icon = icon
        )
        val updatedActions = _state.value.actions + newAction
        manageActionsUseCase.saveActions(updatedActions)
        updateState(_state.value.copy(actions = updatedActions))
    }
    
    fun removeAction(actionId: String) {
        manageActionsUseCase.removeAction(actionId)
        val updatedActions = manageActionsUseCase.getActions()
        updateState(_state.value.copy(actions = updatedActions))
    }
    
    /**
     * Change theme (event up)
     */
    fun changeTheme(newTheme: ThemeMode) {
        viewModelScope.launch {
            try {
                changeThemeUseCase.execute(newTheme)
                println("AppViewModel: Theme change request sent: $newTheme")
            } catch (e: Exception) {
                println("AppViewModel: Error changing theme: ${e.message}")
            }
        }
    }
    
    fun login() {
        println("AppViewModel: login() called - using LoginUseCase...")
        loginUseCase.execute()
        println("AppViewModel: GitHub login initiated")
    }
    
    fun logout() {
        viewModelScope.launch {
            try {
                println("AppViewModel: logout() called - using LogoutUseCase...")
                logoutUseCase.execute()
                println("AppViewModel: Logout completed")
            } catch (e: Exception) {
                println("AppViewModel: Error during logout: ${e.message}")
            }
        }
    }
    
    /**
     * Process OAuth callback - call this when user might have returned from GitHub OAuth
     */
    fun processOAuthCallback() {
        viewModelScope.launch {
            try {
                println("AppViewModel: Processing OAuth callback...")
                observeAuthenticationStateUseCase.processOAuthCallback()
                println("AppViewModel: OAuth callback processing completed")
            } catch (e: Exception) {
                println("AppViewModel: Error processing OAuth callback: ${e.message}")
            }
        }
    }
    
    fun setShowAddActionModal(show: Boolean) {
        updateState(_state.value.copy(showAddActionModal = show))
    }
    
    fun setShowThemeModal(show: Boolean) {
        println("AppViewModel: setShowThemeModal called with: $show")
        updateState(_state.value.copy(showThemeModal = show))
    }
    
    fun clearError() {
        updateState(_state.value.copy(errorMessage = null))
    }
    
    fun setShowOrganizationDiagnosis(show: Boolean) {
        updateState(_state.value.copy(showOrganizationDiagnosis = show))
    }
    
    fun runOrganizationDiagnosis() {
        viewModelScope.launch {
            try {
                updateState(_state.value.copy(isDiagnosingOrganizations = true))
                diagnoseOrganizationAccessUseCase.execute().fold(
                    onSuccess = { diagnosis ->
                        updateState(_state.value.copy(
                            organizationDiagnosis = diagnosis,
                            isDiagnosingOrganizations = false
                        ))
                    },
                    onFailure = { error ->
                        println("AppViewModel: Error diagnosing organization access: ${error.message}")
                        updateState(_state.value.copy(
                            isDiagnosingOrganizations = false,
                            errorMessage = "Failed to diagnose organization access: ${error.message}"
                        ))
                    }
                )
            } catch (e: Exception) {
                println("AppViewModel: Error diagnosing organization access: ${e.message}")
                updateState(_state.value.copy(
                    isDiagnosingOrganizations = false,
                    errorMessage = "Failed to diagnose organization access: ${e.message}"
                ))
            }
        }
    }
    
    fun reauthorizeGitHubApp() {
        // Clear diagnosis state and trigger re-authorization
        updateState(_state.value.copy(
            organizationDiagnosis = null,
            showOrganizationDiagnosis = false
        ))
        // Start the GitHub OAuth flow again
        login()
    }
    
    // =============================================================================
    // ENHANCED FEATURES (CLEAN ARCHITECTURE)
    // =============================================================================
    
    /**
     * Load enhanced GitHub features: organizations, invitations, and enhanced PRs
     * Called when user becomes authenticated
     */
    fun loadEnhancedFeatures() {
        viewModelScope.launch {
            val currentState = _state.value
            if (!currentState.isAuthenticated) {
                println("⚠️ Cannot load enhanced features - user not authenticated")
                return@launch
            }
            
            updateState(currentState.copy(isEnhancedLoading = true))
            
            try {
                // Load organizations
                getPRInsightsUseCase.getUserOrganizations().fold(
                    onSuccess = { orgs ->
                        val updatedState = _state.value.copy(organizations = orgs)
                        updateState(updatedState)
                        println("✅ Loaded ${orgs.size} organizations")
                    },
                    onFailure = { error ->
                        println("⚠️ Failed to load organizations: ${error.message}")
                    }
                )
                
                // Load pending invitations
                getPRInsightsUseCase.getPendingInvitations().fold(
                    onSuccess = { invitations ->
                        val updatedState = _state.value.copy(pendingInvitations = invitations)
                        updateState(updatedState)
                        println("✅ Loaded ${invitations.size} pending invitations")
                    },
                    onFailure = { error ->
                        println("⚠️ Failed to load invitations: ${error.message}")
                    }
                )
                
                // Load enhanced PRs
                loadEnhancedPRs(_state.value.currentViewMode)
                
            } catch (e: Exception) {
                println("🚫 Error loading enhanced features: ${e.message}")
            } finally {
                val finalState = _state.value.copy(isEnhancedLoading = false)
                updateState(finalState)
            }
        }
    }
    
    /**
     * Load enhanced PRs for a specific view mode
     */
    fun loadEnhancedPRs(viewMode: PRViewMode) {
        viewModelScope.launch {
            try {
                getPRInsightsUseCase.execute(viewMode = viewMode).fold(
                    onSuccess = { result ->
                        val updatedState = _state.value.copy(
                            enhancedPRs = result.pullRequests,
                            currentViewMode = viewMode
                        )
                        updateState(updatedState)
                        println("✅ Loaded ${result.pullRequests.size} enhanced PRs for mode: $viewMode")
                    },
                    onFailure = { error ->
                        println("⚠️ Failed to load enhanced PRs: ${error.message}")
                    }
                )
            } catch (e: Exception) {
                println("🚫 Error loading enhanced PRs: ${e.message}")
            }
        }
    }
    
    /**
     * Change the PR view mode and reload enhanced PRs
     */
    fun changeViewMode(viewMode: PRViewMode) {
        val currentState = _state.value
        if (currentState.currentViewMode == viewMode) {
            return // No change needed
        }
        
        updateState(currentState.copy(currentViewMode = viewMode))
        loadEnhancedPRs(viewMode)
    }
    
    /**
     * Refresh all enhanced features data
     */
    fun refreshEnhancedFeatures() {
        loadEnhancedFeatures()
    }
    
    /**
     * Show/hide settings modal
     */
    fun setShowSettingsModal(show: Boolean) {
        val currentState = _state.value
        updateState(currentState.copy(showSettingsModal = show))
    }
    
    // =============================================================================
    // CLEANUP
    // =============================================================================
    
    // Clean up resources
    fun onCleared() {
        viewModelJob.cancel()
    }
    
    private fun generateId(): String {
        return "${(1000000..9999999).random()}-${(1000..9999).random()}"
    }
}
